// src/modules/CqcHq.tsx
import React, { useEffect, useState } from 'react';
import { User } from '../types';
import { AUDIT_TEMPLATES, INTERVIEW_BANK } from '../data/auditLibrary';
import { KEY_QUESTION_META, KeyQuestion } from '../auditTypes';
import { auditApi } from '../services/api';

const CHECKLIST = [
  'CQC registration certificate & Statement of Purpose',
  'Policies index with review dates',
  'Audit folder: completed audits + action plans (closed loop)',
  'Training matrix & supervision records',
  'Sample staff recruitment files (DBS, references)',
  'MARs + CD register',
  'Risk assessments & care plans sample',
  'MCA/DoLS register & authorisations',
  'Safeguarding file + notifications log',
  'Complaints log + responses',
  'Duty of Candour log',
  'Incident/accident trend analysis',
  'Fire logbook & drill records',
  'Legionella/water safety log',
  'Gas, electrical, LOLER, PAT certificates',
  'Staff rota + contingency plan',
  'Resident/relative meeting minutes',
  'Surveys + action plans',
];

export const CqcHq: React.FC<{ user: User }> = () => {
  const [tab, setTab] = useState<'score' | 'checklist' | 'interview' | 'pack'>('score');
  const [audience, setAudience] = useState<'staff' | 'manager' | 'resident'>('staff');
  const [check, setCheck] = useState<Record<string, string>>({});
  const [records, setRecords] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [packKq, setPackKq] = useState<string>('');
  const [pack, setPack] = useState<any>(null);
  const [loadingPack, setLoadingPack] = useState(false);

  useEffect(() => {
    auditApi.list().then(setRecords).catch(() => {});
    auditApi.actionPlans().then(setPlans).catch(() => {});
    auditApi.checklist().then(rows => {
      const m: Record<string, string> = {};
      rows.forEach((r: any) => (m[r.item_key] = r.status));
      setCheck(m);
    }).catch(() => {});
  }, []);

  const saveCheckItem = async (key: string, v: string) => {
    setCheck(c => ({ ...c, [key]: v }));
    try { await auditApi.saveChecklist(key, v); } catch (e: any) { alert(e.message); }
  };

  const loadPack = async (kq: string) => {
    setPackKq(kq); setLoadingPack(true);
    try {
      const data = await auditApi.evidencePack(kq);
      setPack(data);
    } catch (e: any) { alert(e.message); }
    finally { setLoadingPack(false);
      setTimeout(() => window.print(), 400);
    }
  };

  // Compute KQ scores from server-side records
  const kqScores: Record<KeyQuestion, { pts: number; max: number }> = {
    safe: { pts: 0, max: 0 }, effective: { pts: 0, max: 0 }, caring: { pts: 0, max: 0 },
    responsive: { pts: 0, max: 0 }, wellled: { pts: 0, max: 0 },
  };
  for (const r of records) {
    const t = AUDIT_TEMPLATES.find(x => x.id === r.template_id); if (!t) continue;
    for (const q of t.questions) {
      const a = (r.answers || []).find((x: any) => x.question_id === q.id);
      if (!a || a.answer_value === 'na') continue;
      kqScores[q.keyQuestion].max += 1;
      kqScores[q.keyQuestion].pts += a.answer_value === 'yes' ? 1 : (a.answer_value === 'partial' ? 0.5 : 0);
    }
  }
  const pct = (s: { pts: number; max: number }) => (s.max ? Math.round(100 * s.pts / s.max) : null);
  const today = new Date().toISOString().slice(0, 10);
  const adherence = Math.round(100 * AUDIT_TEMPLATES.filter(t => {
    const last = records.filter(r => r.template_id === t.id).sort((a, b) => b.completed_at.localeCompare(a.completed_at))[0];
    if (!last) return false;
    const days = Math.floor((Date.now() - new Date(last.completed_at).getTime()) / 86400000);
    const w = ({ daily: 1, weekly: 7, monthly: 31, quarterly: 92, annual: 366 } as any)[t.frequency];
    return days <= w;
  }).length / AUDIT_TEMPLATES.length);
  const openP = plans.filter(p => p.status !== 'closed');
  const closure = plans.length ? Math.round(100 * (plans.length - openP.length) / plans.length) : 100;
  const scores = (Object.keys(kqScores) as KeyQuestion[]).map(k => pct(kqScores[k])).filter((x): x is number => x !== null);
  const overall = scores.length ? Math.round(0.5 * (scores.reduce((a, b) => a + b, 0) / scores.length) + 0.3 * adherence + 0.2 * closure) : 0;
  const readyCount = CHECKLIST.filter(c => check[c] === 'ready').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#123B36]">CQC HQ</h1>
          <p className="text-sm text-[#6B7A76]">Live inspection-readiness – computed from real activity.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(['score', 'checklist', 'interview', 'pack'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${tab === t ? 'bg-[#123B36] text-white' : 'bg-white border border-[#E3DFD5]'}`}>
              {t === 'score' ? 'Readiness' : t === 'checklist' ? 'Inspection pack' : t === 'pack' ? 'PDF evidence' : 'Mock interviews'}
            </button>
          ))}
        </div>
      </div>

      {tab === 'score' && (
        <div className="space-y-6">
          <div className="bg-[#123B36] text-white rounded-2xl p-6 flex items-center gap-6">
            <div className="text-6xl font-bold">{overall}%</div>
            <div>
              <div className="font-semibold">{overall >= 85 ? 'Outstanding trajectory' : overall >= 70 ? 'Good – push the action plans' : 'Requires improvement – focus on overdue audits & actions'}</div>
              <div className="text-sm opacity-80 mt-1">50% audit scores · 30% audits on schedule · 20% action closure</div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-[#E3DFD5] p-4 space-y-3">
              <h3 className="font-semibold text-[#123B36]">Score by Key Question</h3>
              {(Object.keys(kqScores) as KeyQuestion[]).map(k => {
                const v = pct(kqScores[k]);
                return (
                  <div key={k}>
                    <div className="flex justify-between text-sm">
                      <span>{KEY_QUESTION_META[k].label}</span>
                      <span>{v === null ? 'no data yet' : `${v}%`}</span>
                    </div>
                    <div className="h-2 bg-[#F1EFE9] rounded-full mt-1">
                      <div className="h-2 rounded-full" style={{ width: `${v ?? 0}%`, background: KEY_QUESTION_META[k].color }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="bg-white rounded-xl border border-[#E3DFD5] p-4 space-y-3">
              <h3 className="font-semibold text-[#123B36]">Governance pulse</h3>
              <div className="text-sm">Audits on schedule: <b>{adherence}%</b></div>
              <div className="text-sm">Open actions: <b className={openP.length ? 'text-[#C77800]' : 'text-[#1B7F4D]'}>{openP.length}</b> ({openP.filter(p => p.due_date < today).length} overdue)</div>
              <div className="text-sm">Action closure rate: <b>{closure}%</b></div>
              <div className="text-sm">Inspection pack: <b>{readyCount}/{CHECKLIST.length}</b> ready</div>
            </div>
          </div>
        </div>
      )}

      {tab === 'checklist' && (
        <div className="space-y-2">
          <div className="text-sm text-[#6B7A76] mb-2">{readyCount}/{CHECKLIST.length} documents inspection-ready.</div>
          {CHECKLIST.map(c => (
            <div key={c} className="bg-white rounded-xl border border-[#E3DFD5] p-3 flex items-center justify-between">
              <span className="text-sm">{c}</span>
              <div className="flex gap-1">
                {['ready', 'partial', 'missing'].map(s => (
                  <button key={s} onClick={() => saveCheckItem(c, s)}
                    className={`text-xs px-2 py-1 rounded-full capitalize ${
                      check[c] === s
                        ? (s === 'ready' ? 'bg-[#1B7F4D] text-white' : s === 'partial' ? 'bg-[#C77800] text-white' : 'bg-[#B3261E] text-white')
                        : 'bg-[#F1EFE9]'
                    }`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'interview' && (
        <div className="space-y-3">
          <div className="flex gap-2">
            {(['staff', 'manager', 'resident'] as const).map(a => (
              <button key={a} onClick={() => setAudience(a)}
                className={`px-4 py-1.5 rounded-lg text-sm capitalize ${audience === a ? 'bg-[#123B36] text-white' : 'bg-white border border-[#E3DFD5]'}`}>
                {a}
              </button>
            ))}
          </div>
          {INTERVIEW_BANK.filter(i => i.audience === audience).map((i, n) => (
            <details key={n} className="bg-white rounded-xl border border-[#E3DFD5] p-4">
              <summary className="cursor-pointer font-medium text-[#20302C]">{i.q}</summary>
              <p className="text-sm text-[#1B7F4D] mt-2"><b>Outstanding answer sounds like:</b> {i.good}</p>
            </details>
          ))}
        </div>
      )}

      {tab === 'pack' && (
        <div className="space-y-4">
          <p className="text-sm text-[#6B7A76]">Generate a one-click PDF/print evidence pack per Key Question – for your inspection day.</p>
          <div className="grid md:grid-cols-5 gap-2">
            {(Object.keys(KEY_QUESTION_META) as KeyQuestion[]).map(k => (
              <button key={k} onClick={() => loadPack(k)}
                className="text-white py-3 rounded-xl font-semibold hover:opacity-90"
                style={{ background: KEY_QUESTION_META[k].color }}>
                {KEY_QUESTION_META[k].label}
              </button>
            ))}
          </div>
          {loadingPack && <p className="text-sm text-[#6B7A76]">Building pack...</p>}
          {pack && (
            <div id="pack-print" className="bg-white rounded-xl border border-[#E3DFD5] p-6 space-y-4 print:p-4">
              <div className="border-b border-[#E3DFD5] pb-3">
                <h2 className="text-2xl font-bold" style={{ color: KEY_QUESTION_META[packKq as KeyQuestion].color }}>
                  Evidence Pack — {KEY_QUESTION_META[packKq as KeyQuestion].label}
                </h2>
                <p className="text-xs text-[#6B7A76]">Generated {pack.generated_at} · Roop Cottage Residential Care Home</p>
              </div>
              <div className="grid grid-cols-4 gap-3 text-center">
                <div className="bg-[#F1EFE9] rounded-lg p-2"><div className="text-2xl font-bold">{pack.summary.audit_count}</div><div className="text-xs text-[#6B7A76]">Audits</div></div>
                <div className="bg-[#F1EFE9] rounded-lg p-2"><div className="text-2xl font-bold">{pack.summary.avg_score}%</div><div className="text-xs text-[#6B7A76]">Avg score</div></div>
                <div className="bg-[#F1EFE9] rounded-lg p-2"><div className="text-2xl font-bold">{pack.summary.closed_plans}</div><div className="text-xs text-[#6B7A76]">Closed plans</div></div>
                <div className="bg-[#F1EFE9] rounded-lg p-2"><div className="text-2xl font-bold">{pack.summary.open_plans}</div><div className="text-xs text-[#6B7A76]">Open plans</div></div>
              </div>
              <div className="space-y-4">
                {pack.audits.map((a: any) => (
                  <details key={a.id} open className="border border-[#E3DFD5] rounded-lg p-3">
                    <summary className="cursor-pointer font-semibold">
                      {a.template_name} — {new Date(a.completed_at).toLocaleDateString('en-GB')} by {a.auditor} — <b>{a.score}%</b>
                    </summary>
                    <ul className="mt-2 text-sm space-y-1">
                      {a.answers.map((an: any) => (
                        <li key={an.id}>
                          {an.answer_value === 'yes' ? '✔' : an.answer_value === 'na' ? '—' : '❌'} {an.text}
                          {an.note && <span className="text-[#6B7A76]"> · {an.note}</span>}
                        </li>
                      ))}
                    </ul>
                    {a.evidence.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {a.evidence.map((e: any) => (
                          <img key={e.id} src={`https://roopcottage.co.uk/${e.file_path}`} className="w-24 h-24 object-cover rounded border" alt="evidence" />
                        ))}
                      </div>
                    )}
                  </details>
                ))}
              </div>
              <div>
                <h3 className="font-semibold text-[#123B36] mt-4 mb-2">Action plans raised on these findings</h3>
                {pack.action_plans.length === 0 && <p className="text-sm text-[#6B7A76]">None.</p>}
                <table className="w-full text-sm">
                  <thead><tr className="border-b"><th className="text-left py-1">Issue</th><th className="text-left py-1">Action</th><th className="text-left py-1">Owner</th><th className="text-left py-1">Due</th><th className="text-left py-1">Status</th></tr></thead>
                  <tbody>
                    {pack.action_plans.map((p: any) => (
                      <tr key={p.id} className="border-b border-[#E3DFD5]">
                        <td className="py-1">{p.issue.slice(0, 60)}...</td>
                        <td className="py-1">{p.action_plan.slice(0, 60)}...</td>
                        <td className="py-1">{p.owner_name}</td>
                        <td className="py-1">{p.due_date}</td>
                        <td className="py-1">{p.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={() => window.print()} className="bg-[#123B36] text-white px-4 py-2 rounded-lg print:hidden">Print / Save as PDF</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};